import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { ohmsLaw } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/ohms")({
  component: OhmsPage,
});

function OhmsPage() {
  const [U, setU] = useState<number | "">("");
  const [I, setI] = useState<number | "">("");
  const [R, setR] = useState<number | "">("");

  const result = useMemo(() => {
    const provided = [U, I, R].filter((v) => v !== "").length;
    if (provided !== 2) return null;
    try {
      return ohmsLaw({
        U: U === "" ? undefined : U,
        I: I === "" ? undefined : I,
        R: R === "" ? undefined : R,
      });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [U, I, R]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Ohms lov" subtitle="Oppgi to av U, I og R — den tredje beregnes." />
      <CalcShell
        title="Inndata"
        description="La ett felt stå tomt for å beregne det."
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title={`Beregnet ${result.solvedFor}`}
              rows={[
                { label: "Spenning U", value: `${fmt(result.U)} V`, strong: result.solvedFor === "U" },
                { label: "Strøm I",    value: `${fmt(result.I)} A`, strong: result.solvedFor === "I" },
                { label: "Motstand R", value: `${fmt(result.R)} Ω`, strong: result.solvedFor === "R" },
              ]}
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Ohms lov`} lines={linesFromElectricianNote(`Beregnet verdi`)} />
            </div>
          </>
          ) : (
            <div className="card p-5 text-sm text-muted-foreground">Fyll inn to verdier.</div>
          )
        }
      >
        <Field label="Spenning U" unit="V"><NumberInput value={U} onChange={setU} /></Field>
        <Field label="Strøm I" unit="A"><NumberInput value={I} onChange={setI} /></Field>
        <Field label="Motstand R" unit="Ω"><NumberInput value={R} onChange={setR} /></Field>
      </CalcShell>
    </>
  );
}
